import * as Sentry from '@sentry/react'
import React, { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router'

import AccessControlGuard from './components/guards/AccessControlGuard'
import AuthGuard from './components/guards/AuthGuard'
import CompanyAdminGuard from './components/guards/CompanyAdminGuard'
import CompassGuard from './components/guards/CompassGuard'
import GuestGuard from './components/guards/GuestGuard'
import AppLayout from './components/layouts/AppLayout'
import ClientDashboardLayout from './components/layouts/DashboardLayout/ClientDashboardLayout'
import CompanyDashboardCompanyCompassLayout from './components/layouts/DashboardLayout/CompanyDashboardCompassLayout/CompanyDashboardCompanyCompassLayout'
import CompanyDashboardLayout from './components/layouts/DashboardLayout/CompanyDashboardLayout'
import CompanyDashboardProfessionalNetworkLayout from './components/layouts/DashboardLayout/CompanyDashboardProfessionalNetworkLayout/components/CompanyDashboardProfessionalNetworkLayout'
import GroupDashboardLayout from './components/layouts/DashboardLayout/GroupDashboardLayout'
import IcaewDashboardLayout from './components/layouts/DashboardLayout/IcaewDashboardLayout'
import LandingDarkLayout from './components/layouts/LandingDarkLayout/LandingDarkLayout'
import LandingGreyLayout from './components/layouts/LandingGreyLayout'
import LandingWhiteLayout from './components/layouts/LandingWhiteLayout/LandingWhiteLayout'
import MinimalLayout from './components/layouts/MinimalLayout'
import OfficeLayout from './components/layouts/OfficeLayout/OfficeLayout'
import ReferralInviteLayout from './components/layouts/ReferralInviteLayout/ReferralInviteLayout'
import RqCertifiedLayout from './components/layouts/RqCertifiedLayout/RqCertifiedLayout'
import ReferralWithESignatureDetailsProvider from './components/misc/ReferralWithESignatureDetailsProvider'
import SentryErrorBoundary from './components/misc/SentryErrorBoundary'
import GlobalModals from './components/modals/GlobalModals'
import SkeletonPage from './components/skeleton/SkeletonPage'
import LoadingWidget from './components/widgets/LoadingWidget'
import { usePageViewListener } from './hooks/usePageViewListener'
import { useRestoreScrollPosition } from './hooks/useRestoreScrollPosition'
import { isNotProduction } from './lib/helpers/envHelpers'
import { ROUTES } from './lib/routes'
import ClientPaymentPage from './pages/clientPayment/ClientPaymentPage'
import CompleteRegistrationPage from './pages/CompleteRegistrationPage'
import IndexPage from './pages/IndexPage'
import { USER_TYPE } from './types/misc'

const AddCompanyPage = lazy(() => import('./pages/AddCompanyPage'))

const AddRelationshipPage = lazy(() => import('./pages/AddRelationshipPage'))

const AddExistingAdviserPage = lazy(
  () => import('./pages/AddExistingAdviserPage'),
)

const AddRelationshipAgreementPage = lazy(
  () => import('./pages/addRelationshipAgreement/AddRelationshipAgreementPage'),
)

const BookMeetingPage = lazy(
  () => import('./pages/bookMeeting/BookMeetingPage'),
)

const ChallengePage = lazy(() => import('./pages/challenge/ChallengePage'))

const CommercialAgreementEditPage = lazy(
  () => import('./pages/commercialAgreementEdit/CommercialAgreementEditPage'),
)

const CompanyProfilePage = lazy(
  () => import('./pages/companyProfile/CompanyProfilePage'),
)

const ContactsPage = lazy(() => import('./pages/contacts/ContactsPage'))

const ContactsImportPage = lazy(
  () => import('./pages/contactsImport/ContactsImportPage'),
)

const CompanyCompassPage = lazy(
  () => import('./pages/companyCompass/CompanyCompassPage'),
)

const CompanyCompassViewPage = lazy(
  () => import('./pages/companyCompassView/CompanyCompassViewPage'),
)

const CompanyCompassDashboardPage = lazy(
  () => import('./pages/companyCompassDashboard/CompanyCompassDashboardPage'),
)

const CompanyCompassGlobalLinkPage = lazy(
  () => import('./pages/companyCompassGlobalLink/CompanyCompassGlobalLinkPage'),
)

const CompanyCompassInvitePage = lazy(
  () => import('./pages/companyCompassInvite/CompanyCompassInvitePage'),
)

const CompanyCompassInvitePreviewPage = lazy(
  () =>
    import(
      './pages/companyCompassInvitePreview/CompanyCompassInvitePreviewPage'
    ),
)

const CompanyCompassSnapshotsPage = lazy(
  () => import('./pages/companyCompassSnapshots/CompanyCompassSnapshotsPage'),
)

const CompanySuspendedPage = lazy(() => import('./pages/CompanySuspendedPage'))

const CompassActionsPage = lazy(
  () => import('./pages/compassActions/CompassActionsPage'),
)

const CompassConfirmSharingPage = lazy(
  () => import('./pages/compassConfirmSharing/CompassConfirmSharingPage'),
)

const CompassConfirmStartPage = lazy(
  () => import('./pages/compassConfirmStart/CompassConfirmStartPage'),
)

const CompassConfirmSharingViaGlobalLinkPage = lazy(
  () =>
    import(
      './pages/compassConfirmSharingViaGlobalLink/CompassConfirmSharingViaGlobalLinkPage'
    ),
)

const CompassDashboardPage = lazy(
  () => import('./pages/compassDashboard/CompassDashboardPage'),
)

const CompassDetailsAlreadyOnFilePage = lazy(
  () =>
    import(
      './pages/compassDetailsAlreadyOnFile/CompassDetailsAlreadyOnFilePage'
    ),
)

const CompassHowItWorksPage = lazy(
  () => import('./pages/compassHowItWorks/CompassHowItWorksPage'),
)

const CompassPartnersPage = lazy(
  () => import('./pages/compassPartners/CompassPartnersPage'),
)

const CompassPdfGuidePage = lazy(
  () => import('./pages/compassPdfGuide/CompassPdfGuidePage'),
)

const CompassPdfReportPreviewPage = lazy(
  () => import('./pages/compassPdfReportPreview/CompassPdfReportPreviewPage'),
)

const CompassPdfReportSectionPage = lazy(
  () => import('./pages/compassPdfReportSection/CompassPdfReportSectionPage'),
)

const CompassRedirectPage = lazy(
  () => import('./pages/compassRedirect/CompassRedirectPage'),
)

const CompassSharingPage = lazy(
  () => import('./pages/compassSharing/CompassSharingPage'),
)

const CompassSnapshotPage = lazy(
  () => import('./pages/compassSnapshot/CompassSnapshotPage'),
)

const CompassSnapshotsPage = lazy(
  () => import('./pages/compassSnapshots/CompassSnapshotsPage'),
)

const CompassStartPage = lazy(
  () => import('./pages/compassStart/CompassStartPage'),
)

const CompassUpdatePage = lazy(
  () => import('./pages/compassUpdate/CompassUpdatePage'),
)

const ConfirmConsentPage = lazy(
  () => import('./pages/confirmConsent/ConfirmConsentPage'),
)

const DebugPage = lazy(() => import('./pages/debug/DebugPage'))

const DemoVideosPage = lazy(() => import('./pages/demoVideos/DemoVideosPage'))

const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage'))

const DomainAccessDeniedPage = lazy(
  () => import('./pages/DomainAccessDeniedPage'),
)

const AutoProvisionDomainVerificationPage = lazy(
  () =>
    import(
      './pages/autoProvisionDomainVerification/AutoProvisionDomainVerificationPage'
    ),
)

const EmailAndCalendarIntegrationPage = lazy(
  () =>
    import(
      './pages/emailAndCalendarIntegration/EmailAndCalendarIntegrationPage'
    ),
)

const FindProfessionalPage = lazy(() => import('./pages/FindProfessionalPage'))

const GettingStartedPage = lazy(
  () => import('./pages/gettingStarted/GettingStartedPage'),
)

const MobileLandingPage = lazy(() => import('./pages/mobile/MobileLandingPage'))

const IcaewOverviewPage = lazy(() => import('./pages/IcaewOverviewPage'))

const IcaewFirmSearchPage = lazy(
  () => import('./pages/icaewFirmSearch/IcaewFirmSearchPage'),
)

const IcaewTrendsPage = lazy(() => import('./pages/IcaewTrendsPage'))

const IntellifloIntegrationPage = lazy(
  () => import('./pages/intellifloIntegration/IntellifloIntegrationPage'),
)

const IntellifloIntegrationRedirectPage = lazy(
  () =>
    import(
      './pages/intellifloIntegrationRedirect/IntellifloIntegrationRedirectPage'
    ),
)

const InvalidOrExpiredPage = lazy(() => import('./pages/InvalidOrExpiredPage'))

const CompanyInvitationPage = lazy(
  () => import('./pages/companyInvitation/CompanyInvitationPage'),
)

const InviteUserPage = lazy(() => import('./pages/inviteUser/InviteUserPage'))
const InviteGroupUserPage = lazy(
  () => import('./pages/inviteGroupUser/InviteGroupUserPage'),
)

const ManageUsersPage = lazy(
  () => import('./pages/manageUsers/ManageUsersPage'),
)

const ManageUserProvisioningPage = lazy(
  () => import('./pages/manageUsers/ManageUserProvisioningPage'),
)

const OfficeTaskPanePage = lazy(
  () => import('./pages/officeTaskPane/OfficeTaskPanePage'),
)

const OfficeReferralConfirmationPage = lazy(
  () =>
    import('./pages/officeReferralConfirmation/OfficeReferralConfirmationPage'),
)

const OfficeCheckAuthPage = lazy(
  () => import('./pages/officeTaskPane/OfficeCheckAuthPage'),
)

const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

const PlannrIntegrationPage = lazy(
  () => import('./pages/plannrIntegration/PlannrIntegrationPage'),
)

const PlannrIntegrationRegisterPage = lazy(
  () =>
    import('./pages/plannrIntegrationRegister/PlannrIntegrationRegisterPage'),
)

const PreferencesPage = lazy(
  () => import('./pages/preferences/PreferencesPage'),
)

const ProfessionalNetworkApprovalManagementPage = lazy(
  () =>
    import(
      './pages/professionalNetworkApprovalManagement/ProfessionalNetworkApprovalManagementPage'
    ),
)

const ProfessionalNetworkInviteToRqPage = lazy(
  () =>
    import(
      './pages/professionalNetworkInviteToRq/ProfessionalNetworkInviteToRqPage'
    ),
)

const ProfessionalNetworkLeadAdvisersPage = lazy(
  () =>
    import(
      './pages/professionalNetworkLeadAdvisers/ProfessionalNetworkLeadAdvisersPage'
    ),
)

const ProfessionalNetworkOnboardingPage = lazy(
  () =>
    import(
      './pages/professionalNetworkOnboardingPage/ProfessionalNetworkOnboardingPage'
    ),
)

const ProfessionalNetworkRelationshipsPage = lazy(
  () =>
    import(
      './pages/professionalNetworkRelationships/ProfessionalNetworkRelationshipsPage'
    ),
)

const ProfessionalNetworkApprovalRequestsPage = lazy(
  () =>
    import(
      './pages/professionalNetworkApprovalRequests/ProfessionalNetworkApprovalRequestsPage'
    ),
)

const ProfessionalNetworkStatsPage = lazy(
  () => import('./pages/professionalNetworkStats/ProfessionalNetworkStatsPage'),
)

const ProfessionalNetworkRevenuePage = lazy(
  () =>
    import('./pages/professionalNetworkRevenue/ProfessionalNetworkRevenuePage'),
)

const ReferClientPage = lazy(
  () => import('./pages/referClient/ReferClientPage'),
)

const ReferralClientConsentPdfPage = lazy(
  () =>
    import(
      './pages/referralESignatureConsentPdf/ReferralESignatureConsentPdfPage'
    ),
)

const ReferralClientConsentPdfPreviewPage = lazy(
  () =>
    import(
      './pages/referralESignatureConsentPdfPreview/ReferralESignatureConsentPdfPreviewPage'
    ),
)

const ReferralCommissionConsentPage = lazy(
  () =>
    import('./pages/referralCommissionConsent/ReferralCommissionConsentPage'),
)

const ReferralESignatureConsentPage = lazy(
  () =>
    import('./pages/referralESignatureConsent/ReferralESignatureConsentPage'),
)

const ReferralFeedbackPage = lazy(
  () => import('./pages/referralFeedback/ReferralFeedbackPage'),
)

const ReferralLinkPage = lazy(
  () => import('./pages/referralLink/ReferralLinkPage'),
)

const ReferralsPage = lazy(() => import('./pages/referrals/ReferralsPage'))

const ReferralsDashboardPage = lazy(
  () => import('./pages/referralsDashboard/ReferralsDashboardPage'),
)

const ReferralInvitesPage = lazy(
  () => import('./pages/referralInvites/ReferralInvitesPage'),
)

const ReferralInvitePage = lazy(
  () => import('./pages/referralInvite/ReferralInvitePage'),
)

const ReferralSummaryPage = lazy(
  () => import('./pages/referralSummary/ReferralSummaryPage'),
)

const CompanyRegistrationPage = lazy(
  () => import('./pages/CompanyRegistrationPage'),
)

const NewUserRedirectPage = lazy(() => import('./pages/NewUserRedirectPage'))

const RequestCallbackPage = lazy(
  () => import('./pages/requestCallback/RequestCallbackPage'),
)

const RqCertifiedApplicationStatusPage = lazy(
  () =>
    import(
      './pages/rqCertifiedApplicationStatus/RqCertifiedApplicationStatusPage'
    ),
)

const RqCertifiedApplicationProcessPage = lazy(
  () =>
    import(
      './pages/rqCertifiedApplicationProcess/RqCertifiedApplicationProcessPage'
    ),
)

const RqCertifiedGlossaryPage = lazy(
  () => import('./pages/rqCertifiedGlossary/RqCertifiedGlossaryPage'),
)

const RqCertifiedNdaPdfPage = lazy(
  () => import('./pages/rqCertifiedNdaPdf/RqCertifiedNdaPdfPage'),
)

const RqCertifiedPdfGuidesPage = lazy(
  () => import('./pages/rqCertifiedPdfGuides/RqCertifiedPdfGuidesPage'),
)

const ShortlistPage = lazy(() => import('./pages/ShortlistPage'))

const SignInPage = lazy(() => import('./pages/SignInPage'))

const SignOutPage = lazy(() => import('./pages/SignOutPage'))

const UserCompassPage = lazy(
  () => import('./pages/userCompass/UserCompassPage'),
)

const WalkthroughPage = lazy(
  () => import('./pages/walkthrough/WalkthroughPage'),
)

const WelcomePage = lazy(() => import('./pages/WelcomePage'))

const WithdrawGdprConsentPage = lazy(
  () => import('./pages/withdrawGdprConsent/WithdrawGdprConsentPage'),
)

const InvitationsPage = lazy(
  () => import('./pages/invitations/InvitationsPage'),
)

interface AppRoute {
  path: string
  element: React.ReactNode
}

type GroupDashboardRoute = AppRoute & {
  padded?: boolean
}

type CompanyDashboardRoute = AppRoute & {
  padded?: boolean
}

const accountSetupRoutes: AppRoute[] = [
  {
    path: ROUTES.companyRegistration,
    element: (
      <LandingGreyLayout variant="card">
        <CompanyRegistrationPage />
      </LandingGreyLayout>
    ),
  },
  {
    path: ROUTES.welcome,
    element: (
      <LandingGreyLayout variant="card">
        <WelcomePage />
      </LandingGreyLayout>
    ),
  },
  {
    path: ROUTES.invitations,
    element: (
      <LandingDarkLayout>
        <MinimalLayout>
          <InvitationsPage />
        </MinimalLayout>
      </LandingDarkLayout>
    ),
  },
  {
    path: ROUTES.newUserRedirectPage,
    element: <NewUserRedirectPage />,
  },
]

const guestRoutes: AppRoute[] = [
  {
    path: ROUTES.index,
    element: <IndexPage />,
  },
  {
    path: ROUTES.signIn,
    element: (
      <GuestGuard>
        <SignInPage />
      </GuestGuard>
    ),
  },
  {
    path: ROUTES.signOut,
    element: <SignOutPage />,
  },
]

const groupDashboardRoutes: GroupDashboardRoute[] = [
  { path: ROUTES.revenue, element: <ProfessionalNetworkRevenuePage /> },
  { path: ROUTES.stats, element: <ProfessionalNetworkStatsPage /> },
]

const companyAndGroupDashboardRoutes: Array<
  CompanyDashboardRoute | GroupDashboardRoute
> = [
  { path: ROUTES.referrals, element: <ReferralsPage /> },
  { path: ROUTES.referralsDashboard, element: <ReferralsDashboardPage /> },
  { path: ROUTES.referralSummary, element: <ReferralSummaryPage /> },
  { path: ROUTES.manageUsers, element: <ManageUsersPage /> },
]

const companyDashboardRoutes: CompanyDashboardRoute[] = [
  { path: ROUTES.addCompany, element: <AddCompanyPage /> },
  { path: ROUTES.addExistingAdviser, element: <AddExistingAdviserPage /> },
  {
    path: ROUTES.addRelationshipAgreement,
    element: <AddRelationshipAgreementPage />,
  },
  { path: ROUTES.addRelationship, element: <AddRelationshipPage /> },
  { path: ROUTES.bookMeeting, element: <BookMeetingPage /> },
  {
    path: ROUTES.commercialAgreementEdit,
    element: (
      <Suspense fallback={<SkeletonPage />}>
        <CommercialAgreementEditPage />
      </Suspense>
    ),
  },
  { path: ROUTES.companyProfile, element: <CompanyProfilePage /> },
  {
    path: ROUTES.compassConfirmStart,
    element: <CompassConfirmStartPage />,
  },
  { path: ROUTES.contacts, element: <ContactsPage /> },
  { path: ROUTES.contactsImport, element: <ContactsImportPage /> },
  {
    path: ROUTES.debug,
    element: (
      <AccessControlGuard
        guard={isNotProduction()}
        onDenied={{ redirect: { to: ROUTES.index } }}
      >
        <DebugPage />
      </AccessControlGuard>
    ),
  },
  { path: ROUTES.demoVideos, element: <DemoVideosPage /> },
  { path: ROUTES.disclaimer, element: <DisclaimerPage /> },
  {
    path: ROUTES.emailAndCalendarIntegration,
    element: <EmailAndCalendarIntegrationPage />,
    padded: false,
  },
  { path: ROUTES.findProfessional, element: <FindProfessionalPage /> },
  { path: ROUTES.gettingStarted, element: <GettingStartedPage /> },
  { path: ROUTES.mobileLanding, element: <MobileLandingPage /> },
  { path: ROUTES.manageUsers, element: <ManageUsersPage /> },
  {
    path: ROUTES.manageUserProvisioning,
    element: (
      <CompanyAdminGuard>
        <ManageUserProvisioningPage />
      </CompanyAdminGuard>
    ),
  },
  {
    path: ROUTES.intellifloIntegration,
    element: <IntellifloIntegrationPage />,
  },
  {
    path: ROUTES.intellifloIntegrationRedirect,
    element: <IntellifloIntegrationRedirectPage />,
  },
  { path: ROUTES.preferences, element: <PreferencesPage /> },
  { path: ROUTES.referrals, element: <ReferralsPage /> },
  {
    path: ROUTES.plannrIntegration,
    element: <PlannrIntegrationPage />,
  },
  {
    path: ROUTES.plannrIntegrationRegister,
    element: <PlannrIntegrationRegisterPage />,
  },
  {
    path: ROUTES.referClient,
    element: <ReferClientPage />,
  },
  {
    path: ROUTES.rqCertifiedApplicationProcess,
    element: (
      <RqCertifiedLayout>
        <RqCertifiedApplicationProcessPage />
      </RqCertifiedLayout>
    ),
  },
  {
    path: ROUTES.rqCertifiedApplicationStatus,
    element: (
      <RqCertifiedLayout>
        <RqCertifiedApplicationStatusPage />
      </RqCertifiedLayout>
    ),
  },
  {
    path: ROUTES.rqCertifiedGlossary,
    element: (
      <RqCertifiedLayout>
        <RqCertifiedGlossaryPage />
      </RqCertifiedLayout>
    ),
  },
  {
    path: ROUTES.rqCertifiedPdfGuides,
    element: (
      <RqCertifiedLayout>
        <RqCertifiedPdfGuidesPage />
      </RqCertifiedLayout>
    ),
  },
]

const companyDashboardCompanyCompassRoutes: AppRoute[] = [
  {
    path: ROUTES.companyCompassDashboard,
    element: <CompanyCompassDashboardPage />,
  },
  {
    path: ROUTES.companyCompassGlobalLink,
    element: <CompanyCompassGlobalLinkPage />,
  },
  {
    path: ROUTES.companyCompassSnapshots,
    element: <CompanyCompassSnapshotsPage />,
  },
]

const companyDashboardProfessionalNetworkRoutes: AppRoute[] = [
  {
    path: ROUTES.professionalNetworkApprovalManagement,
    element: <ProfessionalNetworkApprovalManagementPage />,
  },
  {
    path: ROUTES.professionalNetworkInviteToRq,
    element: <ProfessionalNetworkInviteToRqPage />,
  },
  {
    path: ROUTES.professionalNetworkOnboarding,
    element: <ProfessionalNetworkOnboardingPage />,
  },
  {
    path: ROUTES.professionalNetworkRelationships,
    element: <ProfessionalNetworkRelationshipsPage />,
  },
  {
    path: ROUTES.professionalNetworkRequests,
    element: <ProfessionalNetworkApprovalRequestsPage />,
  },
  {
    path: ROUTES.professionalNetworkStats,
    element: <ProfessionalNetworkStatsPage />,
  },
  {
    path: ROUTES.professionalNetworkRevenue,
    element: <ProfessionalNetworkRevenuePage />,
  },
  {
    path: ROUTES.professionalNetworkApprovalManagement,
    element: <ProfessionalNetworkApprovalManagementPage />,
  },
  {
    path: ROUTES.professionalNetworkLeadAdvisers,
    element: <ProfessionalNetworkLeadAdvisersPage />,
  },
]

const clientDashboardRoutes: AppRoute[] = [
  { path: ROUTES.compassAddCompany, element: <AddCompanyPage /> },
  { path: ROUTES.compassActions, element: <CompassActionsPage /> },
  {
    path: ROUTES.compassConfirmSharing,
    element: <CompassConfirmSharingPage />,
  },
  {
    path: ROUTES.compassConfirmSharingViaGlobalLink,
    element: <CompassConfirmSharingViaGlobalLinkPage />,
  },
  { path: ROUTES.compassDashboard, element: <CompassDashboardPage /> },
  {
    path: ROUTES.compassDetailsAlreadyOnFile,
    element: <CompassDetailsAlreadyOnFilePage />,
  },
  { path: ROUTES.compassDisclaimer, element: <DisclaimerPage /> },
  { path: ROUTES.compassHowItWorks, element: <CompassHowItWorksPage /> },
  { path: ROUTES.compassPartners, element: <CompassPartnersPage /> },
  { path: ROUTES.compassSnapshots, element: <CompassSnapshotsPage /> },
  { path: ROUTES.compassSnapshot, element: <CompassSnapshotPage /> },
  { path: ROUTES.compassSharing, element: <CompassSharingPage /> },
  { path: ROUTES.compassStart, element: <CompassStartPage /> },
  { path: ROUTES.compassUpdate, element: <CompassUpdatePage /> },
]

const guestLandingRoutes: AppRoute[] = [
  { path: ROUTES.confirmConsent, element: <ConfirmConsentPage /> },
  {
    path: ROUTES.autoProvisionDomainVerification,
    element: <AutoProvisionDomainVerificationPage />,
  },
  { path: ROUTES.inviteUser, element: <InviteUserPage /> },
  { path: ROUTES.inviteGroupUser, element: <InviteGroupUserPage /> },
  { path: ROUTES.withdrawGdprConsent, element: <WithdrawGdprConsentPage /> },
  { path: ROUTES.clientPayment, element: <ClientPaymentPage /> },
]

const guestLandingRoutesWthBranding: AppRoute[] = [
  { path: ROUTES.requestCallback, element: <RequestCallbackPage /> },
]

const guestLandingWhiteTemplateRoutes: AppRoute[] = [
  { path: ROUTES.companyInvitation, element: <CompanyInvitationPage /> },
  { path: ROUTES.referralFeedback, element: <ReferralFeedbackPage /> },
]

const guestLandingNoTemplateRoutes: AppRoute[] = [
  {
    path: ROUTES.commissionConsent,
    element: <ReferralCommissionConsentPage />,
  },
]

const companyUserLandingRoutes: AppRoute[] = [
  { path: ROUTES.companySuspended, element: <CompanySuspendedPage /> },
  { path: ROUTES.shortlist, element: <ShortlistPage /> },
  {
    path: ROUTES.domainAccessDenied,
    element: (
      <MinimalLayout>
        <DomainAccessDeniedPage />
      </MinimalLayout>
    ),
  },
]

const icaewRoutes: AppRoute[] = [
  { path: ROUTES.icaewDisclaimer, element: <DisclaimerPage /> },
  { path: ROUTES.icaewOverview, element: <IcaewOverviewPage /> },
  { path: ROUTES.icaewFirmSearch, element: <IcaewFirmSearchPage /> },
  { path: ROUTES.icaewTrends, element: <IcaewTrendsPage /> },
]

const referralInviteRoutes: AppRoute[] = [
  { path: ROUTES.referralInvites, element: <ReferralInvitesPage /> },
  {
    path: ROUTES.referralInvite,
    element: <ReferralInvitePage />,
  },
]

const officeRoutes: AppRoute[] = [
  {
    path: ROUTES.officeReferralConfirmation,
    element: <OfficeReferralConfirmationPage />,
  },
  {
    path: ROUTES.officeTaskPane,
    element: <OfficeTaskPanePage />,
  },
]

const miscRoutes: AppRoute[] = [
  { path: ROUTES.compassPdfGuide, element: <CompassPdfGuidePage /> },
  {
    path: ROUTES.companyCompass,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompanyCompassPage />
      </AuthGuard>
    ),
  },
  { path: ROUTES.companyCompassInvite, element: <CompanyCompassInvitePage /> },
  {
    path: ROUTES.companyCompassInvitePreview,
    element: <CompanyCompassInvitePreviewPage />,
  },
  {
    path: ROUTES.companyCompassView,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompanyCompassViewPage />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES.compassPdfReportPreview,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompassPdfReportPreviewPage />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES.compassPdfReportSection,
    element: <CompassPdfReportSectionPage />,
  },
  { path: ROUTES.compassRedirect, element: <CompassRedirectPage /> },
  {
    path: ROUTES.completeRegistration,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompleteRegistrationPage isAtSignup={false} />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES.completeRegistrationAtSignup,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompleteRegistrationPage isAtSignup={true} />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES.officeCheckAuth,
    element: <OfficeCheckAuthPage />,
  },
  {
    path: ROUTES.referralESignatureConsent,
    element: (
      <ReferralWithESignatureDetailsProvider>
        <ReferralESignatureConsentPage />
      </ReferralWithESignatureDetailsProvider>
    ),
  },
  {
    path: ROUTES.referralESignatureConsentPdf,
    element: (
      <ReferralWithESignatureDetailsProvider>
        <ReferralClientConsentPdfPage />
      </ReferralWithESignatureDetailsProvider>
    ),
  },
  {
    path: ROUTES.referralESignatureConsentPdfPreview,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <ReferralClientConsentPdfPreviewPage />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES.referralLink,
    element: <ReferralLinkPage />,
  },
  {
    path: ROUTES.rqCertifiedNdaPdf,
    element: <RqCertifiedNdaPdfPage />,
  },
  {
    path: ROUTES.userCompass,
    element: (
      <CompassGuard>
        <UserCompassPage />
      </CompassGuard>
    ),
  },
  {
    path: ROUTES.walkthrough,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <WalkthroughPage />
      </AuthGuard>
    ),
  },
]

const SentryAwareRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const Root: React.FC = () => {
  usePageViewListener()
  useRestoreScrollPosition()

  return (
    <>
      <SentryAwareRoutes>
        <Route path={ROUTES.challenge} element={<ChallengePage />} />

        <Route element={<AppLayout />}>
          {/* Guest */}
          {guestRoutes.map((route) => (
            <Route path={route.path} element={route.element} key={route.path} />
          ))}

          {/* Account setup */}
          {accountSetupRoutes.map((route) => (
            <Route
              path={route.path}
              element={<AuthGuard>{route.element}</AuthGuard>}
              key={route.path}
            />
          ))}

          {/* Group dashboard */}
          {groupDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.group}>
                  <GroupDashboardLayout padded={route.padded}>
                    {route.element}
                  </GroupDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Company and Group dashboard routes */}
          {companyAndGroupDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard
                  guard={({ currentUserType }) =>
                    currentUserType === USER_TYPE.company ||
                    currentUserType === USER_TYPE.group
                  }
                >
                  {({ currentUserType }) => (
                    <>
                      {currentUserType === USER_TYPE.company ? (
                        <CompanyDashboardLayout padded={route.padded}>
                          {route.element}
                        </CompanyDashboardLayout>
                      ) : (
                        <GroupDashboardLayout padded={route.padded}>
                          {route.element}
                        </GroupDashboardLayout>
                      )}
                    </>
                  )}
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Company dashboard */}
          {companyDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.company}>
                  <CompanyDashboardLayout padded={route.padded}>
                    {route.element}
                  </CompanyDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Company dashboard company compass routes */}
          <Route element={<CompanyDashboardCompanyCompassLayout />}>
            {companyDashboardCompanyCompassRoutes.map((route) => (
              <Route
                path={route.path}
                key={route.path}
                element={route.element}
              />
            ))}
          </Route>

          {/* Company dashboard professional network routes */}
          <Route element={<CompanyDashboardProfessionalNetworkLayout />}>
            {companyDashboardProfessionalNetworkRoutes.map((route) => (
              <Route
                path={route.path}
                key={route.path}
                element={route.element}
              />
            ))}
          </Route>

          {/* Client dashboard */}
          {clientDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.client}>
                  <ClientDashboardLayout>{route.element}</ClientDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Company user landing routes */}
          {companyUserLandingRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.company}>
                  <LandingDarkLayout>{route.element}</LandingDarkLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* ICAEW */}
          {icaewRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.icaew}>
                  <IcaewDashboardLayout>{route.element}</IcaewDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Guest landing */}
          {guestLandingRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <LandingDarkLayout>
                  <MinimalLayout>{route.element}</MinimalLayout>
                </LandingDarkLayout>
              }
              key={route.path}
            />
          ))}

          {guestLandingRoutesWthBranding.map((route) => (
            <Route path={route.path} element={route.element} key={route.path} />
          ))}

          {/* Guest landing - white template */}
          {guestLandingWhiteTemplateRoutes.map((route) => (
            <Route
              path={route.path}
              element={<LandingWhiteLayout>{route.element}</LandingWhiteLayout>}
              key={route.path}
            />
          ))}

          {/* Guest landing - white template */}
          {guestLandingNoTemplateRoutes.map((route) => (
            <Route path={route.path} element={route.element} key={route.path} />
          ))}

          {/* Referral invites */}
          {referralInviteRoutes.map((route) => (
            <Route
              path={route.path}
              key={route.path}
              element={
                <ReferralInviteLayout>{route.element}</ReferralInviteLayout>
              }
            />
          ))}

          {/* Office routes */}
          <Route element={<OfficeLayout />}>
            {officeRoutes.map((route) => (
              <Route
                path={route.path}
                element={route.element}
                key={route.path}
              />
            ))}
          </Route>

          {/* Misc */}
          {miscRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <SentryErrorBoundary
                  key={location.pathname}
                  variant="full-screen"
                >
                  <Suspense fallback={<LoadingWidget />}>
                    {route.element}
                  </Suspense>
                </SentryErrorBoundary>
              }
              key={route.path}
            />
          ))}

          <Route
            path={ROUTES.invalidOrExpired}
            element={<InvalidOrExpiredPage />}
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </SentryAwareRoutes>

      <GlobalModals />
    </>
  )
}

export default Root
